import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { carriesLabMeasurements } from "@ferrivia/api-contract";
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, Text } from "@ferrivia/ui";
import { BOOKING_URL } from "@ferrivia/booking-copy";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation, useFormatters } from "@ferrivia/i18n";
import { useCatalog } from "../../hooks/useCatalog";
import { useOrderConfirmation } from "../../hooks/useOrderConfirmation";
import s from "./OrderConfirmationDialog.module.css";

/**
 * Mounted once in AppShell, so it shows on whichever tab Stripe Checkout
 * returns to. Renders nothing — and fetches nothing — unless the URL carries
 * `?order=&token=`; the inner component then reads its own state from
 * useOrderConfirmation (URL params + a short poll against order-status).
 *
 * A paid lab order ends on "book your appointment", not on "OK". This is the
 * moment a buyer is most likely to book — they have just paid — and the draw
 * is the one thing left.
 */
export function OrderConfirmationDialog() {
  const [searchParams] = useSearchParams();
  if (!searchParams.get("order") || !searchParams.get("token")) return null;
  return <OrderConfirmation />;
}

function OrderConfirmation() {
  const { state: catalog } = useCatalog();
  // Which slugs mean a laboratory appointment is owed. Read off the catalog
  // rather than the wire: `orderStatusResponseSchema` is deliberately thin
  // because anyone holding the success URL can call it, and "this person
  // bought a lab panel" is not something to widen it with.
  const labCarryingSlugs = useMemo(() => {
    const slugs = new Set<string>();
    if (catalog.status === "success") {
      for (const product of catalog.data.products) {
        if (carriesLabMeasurements(product.fulfillmentKind)) slugs.add(product.slug);
      }
    }
    return slugs;
  }, [catalog]);

  const { t } = useTranslation("client");
  const { currencyEur } = useFormatters();
  const tracker = useTracker();
  const { state, dismiss } = useOrderConfirmation();

  // Unknown when the stash is gone (see itemSlugs in useOrderConfirmation) —
  // an empty list reads as "no lab panel", so the dialog keeps its neutral
  // way out rather than sending someone to book an appointment they may not
  // have bought.
  const carriesLab =
    state.status === "paid" && state.itemSlugs.some((slug) => labCarryingSlugs.has(slug));

  const trackCta = (cta: "book_draw" | "done") => {
    tracker.track("store.order_confirmation_cta_clicked", { cta, carries_lab_measurements: carriesLab });
  };

  return (
    <Dialog open={state.status !== "hidden"} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className={s.sheet}>
        {state.status === "loading" && (
          <>
            <DialogTitle className={s.title}>
              {t("store.confirmation.loading.title", "Vahvistetaan maksuasi…")}
            </DialogTitle>
            <DialogDescription className={s.description}>
              {t("store.confirmation.loading.description", "Tämä kestää yleensä vain hetken.")}
            </DialogDescription>
          </>
        )}

        {state.status === "paid" && (
          <>
            <DialogTitle className={s.title}>{t("store.confirmation.paid.title", "Kiitos tilauksesta!")}</DialogTitle>
            <DialogDescription className={s.description}>
              {t("store.confirmation.paid.description", "Maksusi onnistui ja tilauksesi on vahvistettu.")}
            </DialogDescription>
            <Text as="p" size="sm" className={s.meta}>
              {t("store.confirmation.orderNumber", "Tilausnumero: {{orderNumber}}", {
                orderNumber: state.orderNumber,
              })}
            </Text>
            {state.itemNames.length > 0 && (
              <ul className={s.items}>
                {state.itemNames.map((name, index) => (
                  <li key={`${name}-${index}`}>
                    <Text as="span" size="sm">
                      {name}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
            <Text as="p" className={s.total}>
              {currencyEur.format(state.totalCents / 100)}
            </Text>
            {carriesLab ? (
              <div className={s.ways}>
                <Text as="p" size="sm" className={s.description}>
                  {t(
                    "store.confirmation.paid.bookNext",
                    "Seuraava askel on varata näytteenottoaika. Näytteenotto tapahtuu Arvo Terveyden toimipisteissä.",
                  )}
                </Text>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className={s.book}
                  onClick={() => trackCta("book_draw")}
                >
                  {t("store.confirmation.paid.book", "Varaa näytteenottoaika")}
                </a>
                <DialogClose asChild>
                  <Button variant="secondary" className={s.done} onClick={() => trackCta("done")}>
                    {t("store.confirmation.later", "Varaan myöhemmin")}
                  </Button>
                </DialogClose>
              </div>
            ) : (
              <DialogClose asChild>
                <Button variant="primary" className={s.done} onClick={() => trackCta("done")}>
                  {t("store.confirmation.done", "Selvä")}
                </Button>
              </DialogClose>
            )}
          </>
        )}

        {state.status === "timed_out" && (
          <>
            <DialogTitle className={s.title}>
              {t("store.confirmation.timedOut.title", "Käsittelemme yhä maksuasi")}
            </DialogTitle>
            <DialogDescription className={s.description}>
              {t(
                "store.confirmation.timedOut.description",
                "Tilauksesi vahvistuu piakkoin, ja saat siitä vahvistuksen sähköpostiisi.",
              )}
            </DialogDescription>
            <Text as="p" size="sm" className={s.meta}>
              {t("store.confirmation.orderNumber", "Tilausnumero: {{orderNumber}}", {
                orderNumber: state.orderNumber,
              })}
            </Text>
            <DialogClose asChild>
              <Button variant="secondary" className={s.done}>
                {t("store.confirmation.close", "Sulje")}
              </Button>
            </DialogClose>
          </>
        )}

        {state.status === "unsuccessful" && (
          <>
            <DialogTitle className={s.title}>
              {t("store.confirmation.unsuccessful.title", "Maksu ei onnistunut")}
            </DialogTitle>
            <DialogDescription className={s.description}>
              {t(
                "store.confirmation.unsuccessful.description",
                "Tilaustasi ei voitu vahvistaa. Voit yrittää uudestaan.",
              )}
            </DialogDescription>
            <DialogClose asChild>
              <Button variant="secondary" className={s.done}>
                {t("store.confirmation.close", "Sulje")}
              </Button>
            </DialogClose>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
